/* drl-cart.js — rewards bar + desktop summary total refresh (cart PAGE).
   Dawn's cart.js handles qty/remove/section-rerender. We only update the bits
   that live OUTSIDE the re-rendered .js-contents: the rewards bar and the
   desktop in-flow summary. The mobile pay-footer re-renders server-side (it
   sits inside main-cart-footer's .js-contents), so it needs no JS.

   SCOPING: every paint is scoped to a [data-drl-cart-root] (the page's
   <cart-items>). The DRAWER is fully server-rendered on every cart change
   (cart-drawer.js swaps .drawer__inner / cart-drawer-items), so it carries NO
   root marker and JS never touches it — that also stops a /cart page (where
   page + drawer coexist) from painting the page's ladder with the drawer's
   single-tier JSON. Loaded globally via the drawer too, so the header cart
   count updates on add-to-cart from any page. */

(function () {
  'use strict';

  // Loaded by both main-cart-items and cart-drawer; on /cart both run. Guard so
  // we subscribe to cartUpdate once.
  if (window.__drlCartInit) return;
  window.__drlCartInit = true;

  // ── MONEY (canonical Shopify formatter — handles HU "1 234 Ft") ──
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var match = (format || '').match(placeholder);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'"); break;
      default: value = fmt(cents, 2);
    }
    return (format || '{{amount}}').replace(placeholder, value);
  }

  // ── REWARDS LADDER (driven by the pure engine) ──────────
  // SSR paints the initial state; we repaint on every cartUpdate. The engine
  // (drl-rewards-engine.js) decides reached/next; here we only touch the DOM.
  // Tiers JSON is read from WITHIN the root so the page never reads the drawer's
  // single-tier payload. Gift reconciliation (giftsToAdd/Remove) is computed by
  // the engine but NOT acted on yet — that lands with the gift-add phase.
  function getTiers(root) {
    var el = root.querySelector('[data-drl-tiers]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function paintLadder(root, cart) {
    var el = root.querySelector('[data-drl-ladder]');
    if (!el || typeof DrlRewards === 'undefined') return;
    var result = DrlRewards.evaluate(cart, getTiers(root));

    el.style.setProperty('--drl-ladder-progress', result.overallPct + '%');
    el.classList.toggle('is-complete', !result.next && result.stages.length > 0);

    result.stages.forEach(function (s) {
      var rung = el.querySelector('.drl-ladder__rung[data-tier-id="' + s.id + '"]');
      if (rung) rung.classList.toggle('is-reached', s.reached);
    });

    var msg = el.querySelector('[data-drl-ladder-msg]');
    if (!msg) return;
    if (result.next) {
      var left = formatMoney(result.next.remainingCents, el.getAttribute('data-money-format'));
      msg.innerHTML = el.getAttribute('data-msg-progress').replace('[amount]', '<strong>' + left + '</strong>');
    } else {
      msg.textContent = el.getAttribute('data-msg-complete');
    }
  }

  // ── DESKTOP IN-FLOW SUMMARY TOTAL ───────────────────────
  function updateSummaryTotal(root, totalCents) {
    var el = root.querySelector('[data-drl-total]');
    if (!el || isNaN(totalCents)) return;
    el.textContent = formatMoney(totalCents, el.getAttribute('data-money-format'));
  }

  // ── DESKTOP SAVINGS LINE (summed from re-rendered rows) ──
  function updateSavings(root) {
    var el = root.querySelector('[data-drl-savings]');
    if (!el) return;
    var save = 0;
    root.querySelectorAll('.drl-item').forEach(function (r) {
      var c = parseInt(r.getAttribute('data-line-compare'), 10);
      var f = parseInt(r.getAttribute('data-line-final'), 10);
      if (!isNaN(c) && !isNaN(f)) save += c - f;
    });
    el.textContent = '−' + formatMoney(save, el.getAttribute('data-money-format'));
    var row = root.querySelector('[data-drl-savings-row]');
    if (row) row.hidden = save <= 0;
  }

  // ── HEADER CART COUNT (custom header has no cart-icon-bubble) ──
  function updateCartCount(count) {
    var badge = document.querySelector('.hglass__cart-count');
    if (!badge || typeof count !== 'number') return;
    badge.textContent = count;
    badge.classList.toggle('is-hidden', count === 0);
  }

  // The /cart/add.js response (product-form's cartUpdate) carries NO item_count
  // — only the added lines — so reading cart.item_count there blanks the badge
  // (textContent = undefined empties it). Fetch the authoritative count in that
  // case. Qty/remove events publish the full /cart/change.js state (with
  // item_count), so they update synchronously without a fetch.
  function refreshCartCount(cart) {
    if (cart && typeof cart.item_count === 'number') {
      updateCartCount(cart.item_count);
      return;
    }
    var url = (window.routes && routes.cart_url ? routes.cart_url : '/cart') + '.js';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { updateCartCount(c.item_count); })
      .catch(function () {});
  }

  // ── CART CHANGE HOOK (Dawn cart.js publishes cartUpdate) ──
  function onCartUpdate(payload) {
    var cart = payload && payload.cartData;
    if (!cart) return;
    document.querySelectorAll('[data-drl-cart-root]').forEach(function (root) {
      paintLadder(root, cart);
      updateSummaryTotal(root, cart.total_price);
      updateSavings(root);
    });
    refreshCartCount(cart);
  }

  // Initial ladder state is server-rendered; we only repaint on cart changes.
  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
